import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  LiveVideoService,
  LiveVideoData,
} from '../../_services/live-video/live-video.service';

@Component({
    selector: 'app-live-video-player',
        standalone: true,
imports: [CommonModule
    ],
    templateUrl: './live-video-player.component.html',
    styleUrl: './live-video-player.component.scss'
})
export class LiveVideoPlayerComponent implements OnDestroy, AfterViewInit {
  @ViewChild('videoContainer', { static: false }) videoContainer!: ElementRef;
  @ViewChild('videoPlayer', { static: false }) videoPlayer!: ElementRef;
  isPaused: boolean = false;
  auctionStatus = this.isPaused ? 'paused ' : 'live'; // Possible values: 'Live', 'Paused', 'Ended'
  liveVideoData: LiveVideoData | null = null;
  isVisible = false;
  isDragging = false;
  isMinimized = false;
  isMaximized = false;
  isPlaying = false;
  isMuted = true; // Start muted by default
  position = { x: 20, y: 20 };
  dragStart = { x: 0, y: 0 };
  // Store original position and size for restore functionality
  originalPosition = { x: 20, y: 20 };

  private subscriptions = new Subscription();
  private player: any;

  constructor(private liveVideoService: LiveVideoService) {}

  ngAfterViewInit() {
    this.subscriptions.add(
      this.liveVideoService.getLiveVideoData().subscribe((data) => {
        this.liveVideoData = data;
        this.isVisible = !!data; // Set visibility based on data availability
        if (data && data.ivs_playback_url) {
          // Delay to ensure DOM is ready
          setTimeout(() => {
            this.loadVideo(data.ivs_playback_url);
          }, 100);
        } else {
          this.destroyPlayer();
        }
      })
    );

    this.subscriptions.add(
      this.liveVideoService.getVisibility().subscribe((visible) => {
        this.isVisible = visible && !!this.liveVideoData;
      })
    );

    this.subscriptions.add(
      this.liveVideoService.getPosition().subscribe((pos) => {
        this.position = pos;
      })
    );
  }

  private async loadVideo(playbackUrl: string) {
    if (!this.videoPlayer?.nativeElement) {
      console.warn('Video player element not available');
      return;
    }

    try {
      // Load Amazon IVS Player SDK
      if (!(window as any).IVSPlayer) {
        console.log('Loading IVS Player SDK...');
        await this.loadIVSScript();
      }

      this.destroyPlayer(); // Destroy existing player

      // Create new IVS player
      const IVSPlayer = (window as any).IVSPlayer;
      if (!IVSPlayer) {
        throw new Error('IVS Player SDK not loaded');
      }

      console.log('Creating IVS player for:', playbackUrl);
      this.player = IVSPlayer.create();
      this.player.attachHTMLVideoElement(this.videoPlayer.nativeElement);
      this.player.load(playbackUrl);

      // Set up event listeners
      this.player.addEventListener(IVSPlayer.PlayerEventType.READY, () => {
        console.log('IVS Player is ready');
      });

      this.player.addEventListener(
        IVSPlayer.PlayerEventType.ERROR,
        (error: any) => {
          console.error('IVS Player error:', error);
        }
      );

      this.player.addEventListener(IVSPlayer.PlayerEventType.PLAYING, () => {
        this.isPlaying = true;
      });

      this.player.addEventListener(IVSPlayer.PlayerEventType.ENDED, () => {
        this.isPlaying = false;
      });

      // Auto-play with error handling
      this.player
        .play()
        .then(() => {
          this.isPlaying = true;
        })
        .catch((error: any) => {
          console.warn('Auto-play failed:', error);
        });
    } catch (error) {
      console.error('Error loading live video:', error);
    }
  }

  private loadIVSScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(
        'script[src*="amazon-ivs-player"]'
      );
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src =
        'https://player.live-video.net/1.21.0/amazon-ivs-player.min.js';
      script.onload = () => {
        console.log('IVS Player SDK loaded successfully');
        resolve();
      };
      script.onerror = () => {
        console.error('Failed to load IVS Player SDK');
        reject(new Error('Failed to load IVS Player SDK'));
      };
      document.head.appendChild(script);
    });
  }

  private destroyPlayer() {
    if (this.player) {
      this.player.delete();
      this.player = null;
    }
  }

  onMouseDown(event: MouseEvent) {
    if ((event.target as HTMLElement).closest('.video-controls')) {
      return; // Don't start dragging if clicking controls
    }

    // Prevent dragging when maximized
    if (this.isMaximized) {
      return;
    }

    this.isDragging = true;
    this.dragStart = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y,
    };
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;

    const newX = event.clientX - this.dragStart.x;
    const newY = event.clientY - this.dragStart.y;

    // Keep within viewport bounds
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - (this.isMinimized ? 60 : 240);

    this.position = {
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    };

    this.liveVideoService.setPosition(this.position);
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isDragging = false;
  }

  toggleMinimize() {
    this.isMinimized = !this.isMinimized;
  }

  closeLiveVideo() {
    this.liveVideoService.close();
  }

  togglePlayPause() {
    if (!this.player || !this.videoPlayer?.nativeElement) return;

    if (this.isPlaying) {
      try {
        this.player.pause();
        this.isPlaying = false;
      } catch (error) {
        console.warn('Pause failed:', error);
      }
    } else {
      try {
        const playResult = this.player.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult
            .then(() => {
              this.isPlaying = true;
            })
            .catch((error: any) => {
              console.warn('Play failed:', error);
            });
        } else {
          // For cases where play() doesn't return a promise
          this.isPlaying = true;
        }
      } catch (error) {
        console.warn('Play failed:', error);
      }
    }
  }

  toggleMaximize() {
    if (!this.isMaximized) {
      // Store current position before maximizing
      this.originalPosition = { ...this.position };
      // Center and maximize
      this.position = { x: 0, y: 0 };
      this.isMaximized = true;
      this.isMinimized = false;
    } else {
      // Restore to original position
      this.position = { ...this.originalPosition };
      this.isMaximized = false;
    }
    this.liveVideoService.setPosition(this.position);
  }

  toggleMute() {
    if (!this.player || !this.videoPlayer?.nativeElement) return;

    try {
      if (this.isMuted) {
        this.player.setMuted(false);
        this.isMuted = false;
      } else {
        this.player.setMuted(true);
        this.isMuted = true;
      }
    } catch (error) {
      console.warn('Mute toggle failed:', error);
    }
  }

  ngOnDestroy() {
    this.destroyPlayer();
    this.subscriptions.unsubscribe();
  }
}
